import { Plus, Trash2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Tab } from '@headlessui/react';
import { Button } from '@/components/Common/button';

import * as yaml from 'js-yaml';

interface CanonicalExample {
  original: string;
  canonical: string;
  type: string;
}

interface SystemMessagesData {
  intent_responses: {
    greeting: string;
    out_of_scope: string;
    malicious: string;
  };
  sector_domain: {
    name: string;
    description: string;
    expert_role: string;
    assistant_role: string;
  };
  entity_extraction: {
    domain_description: string;
    canonical_examples: CanonicalExample[];
    institutional_examples: CanonicalExample[];
    extraction_confidence_thresholds: {
      high: number;
      medium: number;
      low: number;
      minimum_valid: number;
    };
  };
}

interface SystemMessagesFormProps {
  initialContent?: string;
  onSave: (content: string) => void;
  onCancel: () => void;
}

const DEFAULT_SYSTEM_MESSAGES_DATA: SystemMessagesData = {
  intent_responses: {
    greeting: '',
    out_of_scope: '',
    malicious: '',
  },
  sector_domain: {
    name: '',
    description: '',
    expert_role: '',
    assistant_role: '',
  },
  entity_extraction: {
    domain_description: '',
    canonical_examples: [],
    institutional_examples: [],
    extraction_confidence_thresholds: {
      high: 0.8,
      medium: 0.6,
      low: 0.4,
      minimum_valid: 0.4,
    },
  },
};

export function SystemMessagesForm({ initialContent, onSave, onCancel }: SystemMessagesFormProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<SystemMessagesData>(DEFAULT_SYSTEM_MESSAGES_DATA);

  useEffect(() => {
    if (initialContent) {
      try {
        const parsed = yaml.load(initialContent) as SystemMessagesData;
        if (parsed) {
          setData({
            intent_responses: {
              ...DEFAULT_SYSTEM_MESSAGES_DATA.intent_responses,
              ...(parsed.intent_responses || {}),
            },
            sector_domain: {
              ...DEFAULT_SYSTEM_MESSAGES_DATA.sector_domain,
              ...(parsed.sector_domain || {}),
            },
            entity_extraction: {
              ...DEFAULT_SYSTEM_MESSAGES_DATA.entity_extraction,
              ...(parsed.entity_extraction || {}),
              canonical_examples: parsed.entity_extraction?.canonical_examples || [],
              institutional_examples: parsed.entity_extraction?.institutional_examples || [],
              extraction_confidence_thresholds: {
                ...DEFAULT_SYSTEM_MESSAGES_DATA.entity_extraction.extraction_confidence_thresholds,
                ...(parsed.entity_extraction?.extraction_confidence_thresholds || {}),
              },
            },
          });
        }
      } catch (error) {
        console.error('Error parsing system messages YAML:', error);
      }
    }
  }, [initialContent]);

  const handleAddCanonicalExample = () => {
    setData({
      ...data,
      entity_extraction: {
        ...data.entity_extraction,
        canonical_examples: [
          ...data.entity_extraction.canonical_examples,
          { original: '', canonical: '', type: '' },
        ],
      },
    });
  };

  const handleUpdateCanonicalExample = (index: number, field: keyof CanonicalExample, value: string) => {
    const examples = [...data.entity_extraction.canonical_examples];
    examples[index] = { ...examples[index], [field]: value };
    setData({
      ...data,
      entity_extraction: {
        ...data.entity_extraction,
        canonical_examples: examples,
      },
    });
  };

  const handleDeleteCanonicalExample = (index: number) => {
    setData({
      ...data,
      entity_extraction: {
        ...data.entity_extraction,
        canonical_examples: data.entity_extraction.canonical_examples.filter((_, i) => i !== index),
      },
    });
  };

  const handleAddInstitutionalExample = () => {
    setData({
      ...data,
      entity_extraction: {
        ...data.entity_extraction,
        institutional_examples: [
          ...data.entity_extraction.institutional_examples,
          { original: '', canonical: '', type: '' },
        ],
      },
    });
  };

  const handleUpdateInstitutionalExample = (index: number, field: keyof CanonicalExample, value: string) => {
    const examples = [...data.entity_extraction.institutional_examples];
    examples[index] = { ...examples[index], [field]: value };
    setData({
      ...data,
      entity_extraction: {
        ...data.entity_extraction,
        institutional_examples: examples,
      },
    });
  };

  const handleDeleteInstitutionalExample = (index: number) => {
    setData({
      ...data,
      entity_extraction: {
        ...data.entity_extraction,
        institutional_examples: data.entity_extraction.institutional_examples.filter((_, i) => i !== index),
      },
    });
  };

  const handleSave = () => {
    const yamlContent = yaml.dump(data, {
      indent: 2,
      lineWidth: -1,
      noRefs: true,
      sortKeys: false,
      styles: {
        '!!str': 'literal',
      },
    });

    onSave(yamlContent);
  };

  return (
    <div className="space-y-4">
      <Tab.Group>
        <Tab.List className="flex space-x-1 rounded-xl bg-muted p-1">
          <Tab
            className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-all
              ${selected
                ? 'bg-background text-foreground shadow'
                : 'text-muted-foreground hover:bg-background/60 hover:text-foreground'
              }`
            }
          >
            {t('admin.configForms.systemMessages.tabIntent')}
          </Tab>
          <Tab
            className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-all
              ${selected
                ? 'bg-background text-foreground shadow'
                : 'text-muted-foreground hover:bg-background/60 hover:text-foreground'
              }`
            }
          >
            {t('admin.configForms.systemMessages.tabSector')}
          </Tab>
          <Tab
            className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-all
              ${selected
                ? 'bg-background text-foreground shadow'
                : 'text-muted-foreground hover:bg-background/60 hover:text-foreground'
              }`
            }
          >
            {t('admin.configForms.systemMessages.tabEntityExtraction')}
          </Tab>
        </Tab.List>

        <Tab.Panels className="mt-4">
          {/* Intent Responses Tab */}
          <Tab.Panel>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div>
                <label className="block text-sm font-medium mb-2">
                  {t('admin.configForms.systemMessages.greeting')}
                </label>
                <textarea
                  value={data.intent_responses.greeting}
                  onChange={(e) => setData({
                    ...data,
                    intent_responses: { ...data.intent_responses, greeting: e.target.value }
                  })}
                  rows={8}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm resize-none font-mono"
                  placeholder={t('admin.configForms.systemMessages.greetingPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  {t('admin.configForms.systemMessages.outOfScope')}
                </label>
                <textarea
                  value={data.intent_responses.out_of_scope}
                  onChange={(e) => setData({
                    ...data,
                    intent_responses: { ...data.intent_responses, out_of_scope: e.target.value }
                  })}
                  rows={8}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm resize-none font-mono"
                  placeholder={t('admin.configForms.systemMessages.outOfScopePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  {t('admin.configForms.systemMessages.malicious')}
                </label>
                <textarea
                  value={data.intent_responses.malicious}
                  onChange={(e) => setData({
                    ...data,
                    intent_responses: { ...data.intent_responses, malicious: e.target.value }
                  })}
                  rows={6}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm resize-none font-mono"
                  placeholder={t('admin.configForms.systemMessages.maliciousPlaceholder')}
                />
              </div>
            </div>
          </Tab.Panel>

          {/* Sector Domain Tab */}
          <Tab.Panel>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('admin.configForms.systemMessages.domainName')}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={data.sector_domain.name}
                  onChange={(e) => setData({
                    ...data,
                    sector_domain: { ...data.sector_domain, name: e.target.value }
                  })}
                  placeholder={t('admin.configForms.systemMessages.domainNamePlaceholder')}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('global.description')}
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={data.sector_domain.description}
                  onChange={(e) => setData({
                    ...data,
                    sector_domain: { ...data.sector_domain, description: e.target.value }
                  })}
                  rows={3}
                  placeholder={t('admin.configForms.systemMessages.sectorDescriptionPlaceholder')}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('admin.configForms.systemMessages.expertRole')}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={data.sector_domain.expert_role}
                  onChange={(e) => setData({
                    ...data,
                    sector_domain: { ...data.sector_domain, expert_role: e.target.value }
                  })}
                  placeholder={t('admin.configForms.systemMessages.expertRolePlaceholder')}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('admin.configForms.systemMessages.assistantRole')}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={data.sector_domain.assistant_role}
                  onChange={(e) => setData({
                    ...data,
                    sector_domain: { ...data.sector_domain, assistant_role: e.target.value }
                  })}
                  placeholder={t('admin.configForms.systemMessages.assistantRolePlaceholder')}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
                />
              </div>
            </div>
          </Tab.Panel>

          {/* Entity Extraction Tab */}
          <Tab.Panel>
            <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('admin.configForms.systemMessages.domainDescription')}
                </label>
                <textarea
                  value={data.entity_extraction.domain_description}
                  onChange={(e) => setData({
                    ...data,
                    entity_extraction: { ...data.entity_extraction, domain_description: e.target.value }
                  })}
                  rows={2}
                  placeholder={t('admin.configForms.systemMessages.domainDescriptionPlaceholder')}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm resize-none"
                />
              </div>

              {/* Canonical Examples */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium">
                    {t('admin.configForms.systemMessages.canonicalExamples')}
                  </label>
                  <Button onClick={handleAddCanonicalExample} size="sm" variant="outline">
                    <Plus className="h-4 w-4 mr-1" />
                    {t('global.add')}
                  </Button>
                </div>
                <div className="space-y-3">
                  {data.entity_extraction.canonical_examples.map((example, index) => (
                    <div key={index} className="border border-border rounded-lg p-3 bg-muted/30">
                      <div className="grid grid-cols-3 gap-2 mb-2">
                        <input
                          type="text"
                          value={example.original}
                          onChange={(e) => handleUpdateCanonicalExample(index, 'original', e.target.value)}
                          placeholder={t('admin.configForms.systemMessages.canonicalOriginalPlaceholder')}
                          className="px-2 py-1.5 border border-border rounded bg-background text-sm"
                        />
                        <input
                          type="text"
                          value={example.canonical}
                          onChange={(e) => handleUpdateCanonicalExample(index, 'canonical', e.target.value)}
                          placeholder={t('admin.configForms.systemMessages.canonicalCanonicalPlaceholder')}
                          className="px-2 py-1.5 border border-border rounded bg-background text-sm"
                        />
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={example.type}
                            onChange={(e) => handleUpdateCanonicalExample(index, 'type', e.target.value)}
                            placeholder={t('admin.configForms.systemMessages.canonicalTypePlaceholder')}
                            className="flex-1 px-2 py-1.5 border border-border rounded bg-background text-sm font-mono"
                          />
                          <button
                            onClick={() => handleDeleteCanonicalExample(index)}
                            className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Institutional Examples */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium">
                    {t('admin.configForms.systemMessages.institutionalExamples')}
                  </label>
                  <Button onClick={handleAddInstitutionalExample} size="sm" variant="outline">
                    <Plus className="h-4 w-4 mr-1" />
                    {t('global.add')}
                  </Button>
                </div>
                <div className="space-y-3">
                  {data.entity_extraction.institutional_examples.map((example, index) => (
                    <div key={index} className="border border-border rounded-lg p-3 bg-muted/30">
                      <div className="grid grid-cols-3 gap-2 mb-2">
                        <input
                          type="text"
                          value={example.original}
                          onChange={(e) => handleUpdateInstitutionalExample(index, 'original', e.target.value)}
                          placeholder={t('admin.configForms.systemMessages.institutionalOriginalPlaceholder')}
                          className="px-2 py-1.5 border border-border rounded bg-background text-sm"
                        />
                        <input
                          type="text"
                          value={example.canonical}
                          onChange={(e) => handleUpdateInstitutionalExample(index, 'canonical', e.target.value)}
                          placeholder={t('admin.configForms.systemMessages.institutionalCanonicalPlaceholder')}
                          className="px-2 py-1.5 border border-border rounded bg-background text-sm"
                        />
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={example.type}
                            onChange={(e) => handleUpdateInstitutionalExample(index, 'type', e.target.value)}
                            placeholder={t('admin.configForms.systemMessages.institutionalTypePlaceholder')}
                            className="flex-1 px-2 py-1.5 border border-border rounded bg-background text-sm font-mono"
                          />
                          <button
                            onClick={() => handleDeleteInstitutionalExample(index)}
                            className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Confidence Thresholds */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  {t('admin.configForms.systemMessages.confidenceThresholds')}
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">{t('admin.configForms.systemMessages.thresholdHigh')}</label>
                    <input
                      type="number"
                      value={data.entity_extraction.extraction_confidence_thresholds.high}
                      onChange={(e) => setData({
                        ...data,
                        entity_extraction: {
                          ...data.entity_extraction,
                          extraction_confidence_thresholds: {
                            ...data.entity_extraction.extraction_confidence_thresholds,
                            high: parseFloat(e.target.value) || 0.8
                          }
                        }
                      })}
                      min="0"
                      max="1"
                      step="0.05"
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">{t('admin.configForms.systemMessages.thresholdMedium')}</label>
                    <input
                      type="number"
                      value={data.entity_extraction.extraction_confidence_thresholds.medium}
                      onChange={(e) => setData({
                        ...data,
                        entity_extraction: {
                          ...data.entity_extraction,
                          extraction_confidence_thresholds: {
                            ...data.entity_extraction.extraction_confidence_thresholds,
                            medium: parseFloat(e.target.value) || 0.6
                          }
                        }
                      })}
                      min="0"
                      max="1"
                      step="0.05"
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">{t('admin.configForms.systemMessages.thresholdLow')}</label>
                    <input
                      type="number"
                      value={data.entity_extraction.extraction_confidence_thresholds.low}
                      onChange={(e) => setData({
                        ...data,
                        entity_extraction: {
                          ...data.entity_extraction,
                          extraction_confidence_thresholds: {
                            ...data.entity_extraction.extraction_confidence_thresholds,
                            low: parseFloat(e.target.value) || 0.4
                          }
                        }
                      })}
                      min="0"
                      max="1"
                      step="0.05"
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">{t('admin.configForms.systemMessages.thresholdMinimumValid')}</label>
                    <input
                      type="number"
                      value={data.entity_extraction.extraction_confidence_thresholds.minimum_valid}
                      onChange={(e) => setData({
                        ...data,
                        entity_extraction: {
                          ...data.entity_extraction,
                          extraction_confidence_thresholds: {
                            ...data.entity_extraction.extraction_confidence_thresholds,
                            minimum_valid: parseFloat(e.target.value) || 0.4
                          }
                        }
                      })}
                      min="0"
                      max="1"
                      step="0.05"
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>

      <div className="flex gap-2 justify-end pt-4 border-t border-border">
        <Button onClick={onCancel} variant="outline">
          {t('actions.cancel')}
        </Button>
        <Button onClick={handleSave}>
          {t('actions.save')}
        </Button>
      </div>
    </div>
  );
}
